import type { IconName } from '@fortawesome/fontawesome-svg-core';
import type { Palette, PaletteColor } from '@mui/material';
import type { MessageDescriptor } from 'react-intl';
import { defineMessages } from 'react-intl';

export enum HealthScoreType {
  Cardiovascular = 'cardiovascular-score',
  Focus = 'focus-score',
  Respiratory = 'respiratory-score',
  Immune = 'immunity-score',
  Sleep = 'sleep-quality-score',
  Stress = 'stress-score',
}

export const healthScoreMessages = defineMessages({
  // Cardiovascular
  cardiovascularTitle: {
    id: 'healthScores.cardiovascular.title',
    defaultMessage: 'Cardiovascular Score',
  },
  cardiovascularDesc: {
    id: 'healthScores.info.cardiovascular.desc',
    defaultMessage:
      'Cardiovascular score evaluates your heart health and cardiovascular fitness. The higher the score, the better.',
  },
  cardiovascularCalc: {
    id: 'healthScores.info.cardiovascular.calc',
    defaultMessage:
      'Based on an average of Resting HR, HR Zones, HR Recovery, and Sleep Quality.',
  },
  cardiovascularDisc: {
    id: 'healthScores.cardiovascular.disclaimer',
    defaultMessage:
      'No cardiovascular data available. Connect a device that tracks heart rate and sleep quality.',
  },

  // Focus
  focusTitle: {
    id: 'healthScores.focus.title',
    defaultMessage: 'Focus Score',
  },
  focusDesc: {
    id: 'healthScores.info.focus.desc',
    defaultMessage:
      'Focus score measures your cognitive readiness and focus potential. A higher score indicates better stress management.',
  },
  focusCalc: {
    id: 'healthScores.info.focus.calc',
    defaultMessage:
      'Based on an average of Heart Rate Variability (HRV) and Sleep Quality.',
  },
  focusDisc: {
    id: 'healthScores.focus.disclaimer',
    defaultMessage:
      'No focus data available. Connect a device that tracks heart rate variability and sleep quality.',
  },

  // Immune
  immuneTitle: {
    id: 'healthScores.immune.title',
    defaultMessage: 'Immune Index',
  },
  immuneDesc: {
    id: 'healthScores.info.immune.desc',
    defaultMessage:
      'Immune Index flags physiological signs of illness or high immune-system stress.',
  },
  immuneCalc: {
    id: 'healthScores.info.immune.calc',
    defaultMessage:
      'Based on a weighted average of: Body Temperature, Heart Rate Variability (HRV), and Respiratory Rate.',
  },
  immuneDisc: {
    id: 'healthScores.immune.disclaimer',
    defaultMessage:
      'No immune index available. Connect a device that tracks heart rate variability and respiratory rate.',
  },

  // Respiratory
  respiratoryTitle: {
    id: 'healthScores.respiratory.title',
    defaultMessage: 'Respiratory Score',
  },
  respiratoryDesc: {
    id: 'healthScores.info.respiratory.desc',
    defaultMessage:
      'Respiratory score monitors your breathing patterns and respiratory health. A higher score indicates better respiratory function',
  },
  respiratoryCalc: {
    id: 'healthScores.info.respiratory.calc',
    defaultMessage:
      'Based on a weighted average of: Oxygen Saturation, Respiratory Rate, and Activity Level.',
  },
  respiratoryDisc: {
    id: 'healthScores.respiratory.disclaimer',
    defaultMessage:
      'No respiratory data available. Connect a device that tracks respiratory rate and oxygen saturation.',
  },
  respiratoryExcellent: {
    id: 'healthScores.info.respiratoryExcellent',
    defaultMessage:
      'Indicates highly efficient respiratory function and strong cardiorespiratory fitness.',
  },
  respiratoryModerate: {
    id: 'healthScores.info.respiratoryModerate',
    defaultMessage:
      'Good function, with potential opportunities for improvement through focused breathing or conditioning.',
  },
  respiratoryPoor: {
    id: 'healthScores.info.respiratoryPoor',
    defaultMessage:
      'Suggests potential deconditioning or inefficient breathing patterns that may be limiting performance.',
  },

  // Sleep
  sleepTitle: {
    id: 'healthScores.sleep.title',
    defaultMessage: 'Sleep Score',
  },
  sleepDesc: {
    id: 'healthScores.info.sleep.desc',
    defaultMessage:
      'The sleep score measures your sleep quality based on duration, stages and recovery metrics. A higher sleep score indicates more effective sleep',
  },
  sleepCalc: {
    id: 'healthScores.info.sleep.calc',
    defaultMessage:
      'Based on a weighted average of: Deep Sleep, REM Sleep, Light Sleep, Total Sleep (mandatory), Body Temperature, Heart Rate Variability (HRV), Resting Heart Rate and Oxygen Saturation (optional).',
  },
  sleepDisc: {
    id: 'healthScores.sleep.disclaimer',
    defaultMessage:
      'No sleep data available. Connect a device that tracks sleep duration and quality.',
  },
  sleepExcellent: {
    id: 'healthScores.info.sleepExcellent',
    defaultMessage:
      'Your sleep was highly efficient, restorative, and well-structured.',
  },
  sleepFair: {
    id: 'healthScores.info.sleepFair',
    defaultMessage:
      'Your sleep is providing some recovery, but there are clear opportunities for improvement.',
  },
  sleepPoor: {
    id: 'healthScores.info.sleepPoor',
    defaultMessage:
      "Indicates that your sleep is likely not meeting your body's recovery demands.",
  },

  // Stress
  stressTitle: {
    id: 'healthScores.stress.title',
    defaultMessage: 'Stress Score',
  },
  stressDesc: {
    id: 'healthScores.info.stress.desc',
    defaultMessage:
      'The stress score tracks your daily stress levels based on physiological markers.',
  },
  stressCalc: {
    id: 'healthScores.info.stress.calc',
    defaultMessage:
      'Based on a weighted average of: Heart Rate, Heart Rate Variability (HRV), Sleep Quality and Activity Level.',
  },
  stressDisc: {
    id: 'healthScores.stress.disclaimer',
    defaultMessage:
      'No stress data available. Connect a device that tracks heart rate variability and sleep quality.',
  },

  //Factor Labels
  factorAverageHR: {
    id: 'healthScores.factors.averageHR',
    defaultMessage: 'Average HR',
  },
  factorAverageHeartRate: {
    id: 'healthScores.factors.averageHeartRate',
    defaultMessage: 'Average Heart Rate',
  },
  factorRestingHR: {
    id: 'healthScores.factors.restingHR',
    defaultMessage: 'Resting HR',
  },
  factorRestingHeartRate: {
    id: 'healthScores.factors.restingHeartRate',
    defaultMessage: 'Resting Heart Rate',
  },
  factorHRZones: {
    id: 'healthScores.factors.hrZones',
    defaultMessage: 'HR Zones',
  },
  factorHRRecovery: {
    id: 'healthScores.factors.hrRecovery',
    defaultMessage: 'HR Recovery',
  },
  factorSleepQuality: {
    id: 'healthScores.factors.sleepQuality',
    defaultMessage: 'Sleep Quality & Quantity',
  },
  factorHRV: {
    id: 'healthScores.factors.hrv',
    defaultMessage: 'HRV',
  },
  factorHeartRateVariability: {
    id: 'healthScores.factors.heartRateVariability',
    defaultMessage: 'Heart Rate Variability (HRV)',
  },
  factorTemperature: {
    id: 'healthScores.factors.temperature',
    defaultMessage: 'Body Temperature',
  },
  factorRespirationRate: {
    id: 'healthScores.factors.respirationRate',
    defaultMessage: 'Respiratory Rate',
  },
  factorOxygen: {
    id: 'healthScores.factors.oxygen',
    defaultMessage: 'Oxygen Saturation',
  },
  factorActivityLevel: {
    id: 'healthScores.factors.activityLevel',
    defaultMessage: 'Activity Level',
  },
  factorDeepSleep: {
    id: 'healthScores.factors.deepSleep',
    defaultMessage: 'Deep Sleep',
  },
  factorRemSleep: {
    id: 'healthScores.factors.remSleep',
    defaultMessage: 'REM Sleep',
  },
  factorLightSleep: {
    id: 'healthScores.factors.lightSleep',
    defaultMessage: 'Light Sleep',
  },
  factorTotalSleep: {
    id: 'healthScores.factors.totalSleep',
    defaultMessage: 'Total Sleep',
  },
  factorTrainingLoad: {
    id: 'healthScores.factors.trainingLoad',
    defaultMessage: 'Activity Level',
  },

  // Quality labels
  excellent: {
    id: 'GLOBAL.excellent',
    defaultMessage: 'Excellent',
  },
  fair: {
    id: 'GLOBAL.fair',
    defaultMessage: 'Fair',
  },
  moderate: {
    id: 'GLOBAL.moderate',
    defaultMessage: 'Moderate',
  },
  poor: {
    id: 'GLOBAL.poor',
    defaultMessage: 'Poor',
  },
});

export type PaletteColorKey =
  | 'primary'
  | 'secondary'
  | 'error'
  | 'warning'
  | 'info'
  | 'success'
  | 'teal'
  | 'purple'
  | 'pink';

/**
 * Defines a numeric threshold for a health score.
 * Used to determine color and message for a given score range.
 */
export interface Threshold {
  /** Friendly name of the threshold (e.g., Excellent, Fair, Poor) */
  label: MessageDescriptor;
  /** Lower bound of this threshold range */
  lower: number;
  /** Upper bound of this threshold range */
  upper: number;
  /** Whether the lower bound is inclusive. Defaults to true */
  lowerInclusive?: boolean;
  /** Whether the upper bound is inclusive. Defaults to true */
  upperInclusive?: boolean;
  /** Description message for this threshold */
  message: MessageDescriptor;
  /** Theme color key (must exist in MUI Palette) */
  color: PaletteColorKey;
}

/**
 * Information about a specific health score contributor.
 */
export interface HealthScoreInfo extends HealthScoreConfigItem {
  /** Display title */
  title: MessageDescriptor;
  /** Description for the score */
  desc: MessageDescriptor;
  /** Calculation explanation */
  calc: MessageDescriptor;
  /** Disclaimer when score is not available  */
  disc: MessageDescriptor;
  /** Contributing factors */
}

/**
 * Internal configuration for a specific health score contributor.
 */
interface HealthScoreConfigItem {
  /** Health score icon */
  icon: IconName;
  /** Contributing factor message IDs used in doughnut chart */
  chartFactors: string[];
  /** Contributing factor message IDs used in modal description (if undefined, default to chartFactors) */
  factors?: string[];
  /** Percentage of how much this factor contributes to the health score calculation */
  data: number[];
  /** Threshold ranges for coloring and messaging */
  thresholds: Threshold[];
  /** Colour of the factors in the chart */
  colors: { primary: keyof Palette; secondary?: keyof PaletteColor }[];
}

type HealthScoreContributorMap = Record<string, HealthScoreConfigItem>;
export const HEALTH_SCORE_CONTRIBUTOR_MAP = (): HealthScoreContributorMap => ({
  [HealthScoreType.Cardiovascular]: {
    icon: 'heart',
    chartFactors: [
      healthScoreMessages.factorSleepQuality.id,
      healthScoreMessages.factorHRZones.id,
      healthScoreMessages.factorHRRecovery.id,
      healthScoreMessages.factorRestingHR.id,
    ],
    factors: [
      healthScoreMessages.factorSleepQuality.id,
      healthScoreMessages.factorHRZones.id,
      healthScoreMessages.factorHRRecovery.id,
      healthScoreMessages.factorRestingHeartRate.id,
    ],
    colors: [
      { primary: 'primary', secondary: 'dark' },
      { primary: 'purple' },
      { primary: 'info' },
      { primary: 'teal' },
    ],
    data: [25, 25, 25, 25],
    thresholds: [],
  },
  [HealthScoreType.Focus]: {
    icon: 'brain',
    chartFactors: [
      healthScoreMessages.factorHRV.id,
      healthScoreMessages.factorSleepQuality.id,
    ],
    factors: [
      healthScoreMessages.factorHeartRateVariability.id,
      healthScoreMessages.factorSleepQuality.id,
    ],
    colors: [{ primary: 'primary', secondary: 'dark' }, { primary: 'purple' }],
    data: [50, 50],
    thresholds: [],
  },
  [HealthScoreType.Immune]: {
    icon: 'shield',
    chartFactors: [
      healthScoreMessages.factorTemperature.id,
      healthScoreMessages.factorHRV.id,
      healthScoreMessages.factorRespirationRate.id,
    ],
    factors: [
      healthScoreMessages.factorTemperature.id,
      healthScoreMessages.factorHeartRateVariability.id,
      healthScoreMessages.factorRespirationRate.id,
    ],
    colors: [{ primary: 'purple' }, { primary: 'info' }, { primary: 'teal' }],
    data: [33.3, 33.3, 33.3],
    thresholds: [],
  },
  [HealthScoreType.Respiratory]: {
    icon: 'wind',
    chartFactors: [
      healthScoreMessages.factorActivityLevel.id,
      healthScoreMessages.factorOxygen.id,
      healthScoreMessages.factorRespirationRate.id,
    ],
    factors: [
      healthScoreMessages.factorActivityLevel.id,
      healthScoreMessages.factorOxygen.id,
      healthScoreMessages.factorRespirationRate.id,
    ],
    colors: [{ primary: 'purple' }, { primary: 'pink' }, { primary: 'teal' }],
    data: [55, 35, 10],
    thresholds: [
      {
        label: healthScoreMessages.excellent,
        lower: 90,
        upper: 100,
        lowerInclusive: true,
        upperInclusive: true,
        message: healthScoreMessages.respiratoryExcellent,
        color: 'success',
      },
      {
        label: healthScoreMessages.fair,
        lower: 70,
        upper: 90,
        lowerInclusive: true,
        upperInclusive: false,
        message: healthScoreMessages.respiratoryModerate,
        color: 'warning',
      },
      {
        label: healthScoreMessages.poor,
        lower: 0,
        upper: 70,
        lowerInclusive: true,
        upperInclusive: false,
        message: healthScoreMessages.respiratoryPoor,
        color: 'error',
      },
    ],
  },
  [HealthScoreType.Sleep]: {
    icon: 'moon',
    chartFactors: [
      healthScoreMessages.factorTemperature.id,
      healthScoreMessages.factorRestingHR.id,
      healthScoreMessages.factorTotalSleep.id,
      healthScoreMessages.factorDeepSleep.id,
      healthScoreMessages.factorRemSleep.id,
      healthScoreMessages.factorLightSleep.id,
      healthScoreMessages.factorHRV.id,
      healthScoreMessages.factorOxygen.id,
    ],
    factors: [
      healthScoreMessages.factorTemperature.id,
      healthScoreMessages.factorRestingHeartRate.id,
      healthScoreMessages.factorTotalSleep.id,
      healthScoreMessages.factorDeepSleep.id,
      healthScoreMessages.factorRemSleep.id,
      healthScoreMessages.factorLightSleep.id,
      healthScoreMessages.factorHeartRateVariability.id,
      healthScoreMessages.factorOxygen.id,
    ],
    colors: [
      { primary: 'purple' },
      { primary: 'teal' },
      { primary: 'info', secondary: 900 },
      { primary: 'primary', secondary: 'dark' },
      { primary: 'primary', secondary: 'main' },
      { primary: 'primary', secondary: 'light' },
      { primary: 'info' },
      { primary: 'pink' },
    ],
    data: [12.5, 12.5, 12.5, 12.5, 12.5, 12.5, 12.5, 12.5],
    thresholds: [
      {
        label: healthScoreMessages.excellent,
        lower: 90,
        upper: 100,
        lowerInclusive: true,
        upperInclusive: true,
        message: healthScoreMessages.sleepExcellent,
        color: 'success',
      },
      {
        label: healthScoreMessages.fair,
        lower: 70,
        upper: 90,
        lowerInclusive: true,
        upperInclusive: false,
        message: healthScoreMessages.sleepFair,
        color: 'warning',
      },
      {
        label: healthScoreMessages.poor,
        lower: 0,
        upper: 70,
        lowerInclusive: true,
        upperInclusive: false,
        message: healthScoreMessages.sleepPoor,
        color: 'error',
      },
    ],
  },
  [HealthScoreType.Stress]: {
    icon: 'brain',
    chartFactors: [
      healthScoreMessages.factorActivityLevel.id,
      healthScoreMessages.factorHRV.id,
      healthScoreMessages.factorAverageHR.id,
      healthScoreMessages.factorSleepQuality.id,
    ],
    factors: [
      healthScoreMessages.factorActivityLevel.id,
      healthScoreMessages.factorHeartRateVariability.id,
      healthScoreMessages.factorAverageHeartRate.id,
      healthScoreMessages.factorSleepQuality.id,
    ],
    colors: [
      { primary: 'purple', secondary: 'main' },
      { primary: 'info', secondary: 'main' },
      { primary: 'teal', secondary: 'main' },
      { primary: 'primary', secondary: 'dark' },
    ],
    data: [25, 25, 25, 25],
    thresholds: [],
  },
});

export const getHealthScoreInfo = (
  metricId: HealthScoreType | string
): HealthScoreInfo => {
  const config = HEALTH_SCORE_CONTRIBUTOR_MAP();

  switch (metricId) {
    case HealthScoreType.Cardiovascular:
      return {
        title: healthScoreMessages.cardiovascularTitle,
        desc: healthScoreMessages.cardiovascularDesc,
        calc: healthScoreMessages.cardiovascularCalc,
        disc: healthScoreMessages.cardiovascularDisc,
        ...config[HealthScoreType.Cardiovascular],
      };

    case HealthScoreType.Focus:
      return {
        title: healthScoreMessages.focusTitle,
        desc: healthScoreMessages.focusDesc,
        calc: healthScoreMessages.focusCalc,
        disc: healthScoreMessages.focusDisc,
        ...config[HealthScoreType.Focus],
      };

    case HealthScoreType.Immune:
      return {
        title: healthScoreMessages.immuneTitle,
        desc: healthScoreMessages.immuneDesc,
        calc: healthScoreMessages.immuneCalc,
        disc: healthScoreMessages.immuneDisc,
        ...config[HealthScoreType.Immune],
      };

    case HealthScoreType.Respiratory:
      return {
        title: healthScoreMessages.respiratoryTitle,
        desc: healthScoreMessages.respiratoryDesc,
        calc: healthScoreMessages.respiratoryCalc,
        disc: healthScoreMessages.respiratoryDisc,
        ...config[HealthScoreType.Respiratory],
      };

    case HealthScoreType.Sleep:
      return {
        title: healthScoreMessages.sleepTitle,
        desc: healthScoreMessages.sleepDesc,
        calc: healthScoreMessages.sleepCalc,
        disc: healthScoreMessages.sleepDisc,
        ...config[HealthScoreType.Sleep],
      };

    case HealthScoreType.Stress:
      return {
        title: healthScoreMessages.stressTitle,
        desc: healthScoreMessages.stressDesc,
        calc: healthScoreMessages.stressCalc,
        disc: healthScoreMessages.stressDisc,
        ...config[HealthScoreType.Stress],
      };

    default:
      return {
        icon: 'heart',
        title: {} as MessageDescriptor,
        desc: {} as MessageDescriptor,
        calc: {} as MessageDescriptor,
        disc: {} as MessageDescriptor,
        chartFactors: [],
        data: [],
        colors: [],
        thresholds: [],
      };
  }
};
